import { GraduationCap, Eye, Scale, UserCheck, TrendingUp } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BUSINESS } from "@/lib/constants";

const values = [
  { icon: GraduationCap, label: "Education First", testId: "value-education-first" },
  { icon: Eye, label: "Transparency", testId: "value-transparency" },
  { icon: Scale, label: "Responsible Financial Decisions", testId: "value-responsible-decisions" },
  { icon: UserCheck, label: "Personalized Guidance", testId: "value-personalized-guidance" },
  { icon: TrendingUp, label: "Long-Term Understanding", testId: "value-long-term" },
];

export default function AboutPage() {
  usePageMeta(
    "About J11 Consulting | Credit & Financial Education",
    "J11 Consulting LLC is a Palm Bay, Florida company providing credit education, financial literacy coaching and funding readiness mentorship for individuals and entrepreneurs."
  );

  return (
    <PageLayout
      eyebrow={`About ${BUSINESS.name}`}
      title="Financial Knowledge Creates Better Decisions."
      subtitle="Education and mentorship for individuals and entrepreneurs who want to understand credit, strengthen their financial foundation and prepare for future opportunities."
      testId="about-page"
    >
      <section className="bg-[#0E0E12] py-14 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <img
              src="https://images.pexels.com/photos/5816285/pexels-photo-5816285.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="A mentor reviewing financial information together with a client"
              data-testid="about-page-image"
              className="aspect-[4/3] w-full rounded-2xl object-cover shadow-xl shadow-black/40"
              loading="lazy"
            />
          </Reveal>
          <div>
            <Reveal>
              <p className="text-base leading-relaxed text-[#B8B2A6] sm:text-lg" data-testid="about-page-paragraph-1">
                {BUSINESS.name} was created to help individuals and entrepreneurs better understand
                areas of finance that are often confusing or poorly explained.
              </p>
              <p className="mt-5 text-base leading-relaxed text-[#B8B2A6] sm:text-lg" data-testid="about-page-paragraph-2">
                Our approach combines education, mentorship and practical guidance so clients can
                better understand their credit profile, financial habits and funding readiness.
              </p>
              <p className="mt-5 text-base leading-relaxed text-[#B8B2A6] sm:text-lg" data-testid="about-page-paragraph-3">
                Instead of simply telling clients what steps to take, J11 focuses on helping them
                understand WHY those steps matter.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <h2 className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-[#8D877B]">
                Our Core Values
              </h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {values.map((value) => (
                  <li
                    key={value.label}
                    data-testid={value.testId}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#141419] px-4 py-3.5"
                  >
                    <value.icon className="h-4 w-4 shrink-0 text-[#D8B54A]" strokeWidth={1.8} />
                    <span className="text-sm font-medium text-[#F2EEE3]">{value.label}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="bg-[#141419] py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-[#17171C] p-7 sm:p-9" data-testid="about-page-identity">
              <p className="font-heading text-lg font-semibold text-[#F2EEE3]">{BUSINESS.name}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#B8B2A6]">
                {BUSINESS.addressLine1}
                <br />
                {BUSINESS.addressLine2}
              </p>
              <a
                href={BUSINESS.phoneHref}
                data-testid="about-page-phone-link"
                className="mt-2 inline-block text-sm font-semibold text-[#D8B54A] hover:text-[#E4C463]"
              >
                {BUSINESS.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </PageLayout>
  );
}
